import { useEffect, useRef } from 'react';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { CartItems } from '@/ui/store/cart/types/CartItems';

interface UseTrackViewCartOptions {
  enabled: boolean;
  currency?: string;
  value: number;
  /** If true, resets the tracking flag when enabled becomes false */
  resetOnDisable?: boolean;
  /** Optional condition to skip tracking even if enabled (e.g., skip if add-to-cart triggered drawer) */
  skipWhen?: boolean;
}

/**
 * Tracks GA4 view_cart event when cart is viewed
 * @param cartItems - Cart items to track
 * @param options - Tracking configuration
 */
export function useTrackViewCart(cartItems: CartItems, options: UseTrackViewCartOptions) {
  const { enabled, currency, value, resetOnDisable = false, skipWhen = false } = options;
  const viewCartFired = useRef(false);

  useEffect(() => {
    if (!enabled || skipWhen) {
      if (resetOnDisable) {
        viewCartFired.current = false;
      }
      return;
    }

    if (viewCartFired.current) {
      return;
    }

    viewCartFired.current = true;

    const ga4Items = DataLayerHelper.mapCartToGA4Items(cartItems);
    DataLayerHelper.pushViewCart(ga4Items, currency, value);
  }, [enabled, skipWhen, cartItems, currency, value, resetOnDisable]);
}
